import { callFunction } from '../utils/cloudbase';

const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_INFO_KEY = 'user_info';

class AuthService {
  // 系统登录
  async login(username, password) {
    try {

      const result = await callFunction('login', { username, password });

      if (result.code === 0) {
        const { accessToken, refreshToken, user } = result.data;
        this.setTokens(accessToken, refreshToken);
        this.setUserInfo(user);
        return { success: true, data: user };
      } else {
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: error.message || '登录失败，请稍后重试' };
    }
  }

  // 刷新令牌
  async refreshToken() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return { success: false, message: '未找到刷新令牌' };
    }

    try {
      const result = await callFunction('login', { refreshToken });

      if (result.code === 0) {
        const { accessToken, refreshToken: newRefreshToken } = result.data;
        this.setTokens(accessToken, newRefreshToken);
        return { success: true };
      } else {
        this.logout();
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Refresh token error:', error);
      this.logout();
      return { success: false, message: '刷新令牌失败' };
    }
  }

  // 获取用户信息
  async getUserInfo() {
    try {
      const userInfo = localStorage.getItem(USER_INFO_KEY);
      if (!userInfo) {
        return null;
      }

      // 检查令牌是否过期
      if (this.isTokenExpired()) {
        // 尝试刷新令牌
        const refreshResult = await this.refreshToken();
        if (!refreshResult.success) {
          this.logout();
          return null;
        }
      }

      return JSON.parse(userInfo);
    } catch (error) {
      console.error('Get user info error:', error);
      this.logout();
      return null;
    }
  }

  // 获取访问令牌
  getAccessToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  // 获取刷新令牌
  getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  // 设置令牌
  setTokens(accessToken, refreshToken) {
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }

  // 设置用户信息
  setUserInfo(userInfo) {
    localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
  }

  // 登出
  async logout() {
    try {
      // 获取访问令牌
      const accessToken = this.getAccessToken();
      if (!accessToken) {
        // 如果没有访问令牌，直接清除本地存储
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(USER_INFO_KEY);
        return { success: true };
      }
      
      // 调用云函数登出
      const result = await callFunction('logout');
      
      if (result.code === 0) {
        // 清除本地存储
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(USER_INFO_KEY);
        return { success: true };
      } else {
        // 即使云函数调用失败，也清除本地存储
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(USER_INFO_KEY);
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Logout error:', error);
      // 发生错误时也清除本地存储
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_INFO_KEY);
      return { success: false, message: error.message || '登出失败，请稍后重试' };
    }
  }

  // 检查是否已登录
  isAuthenticated() {
    return !!this.getAccessToken() && !!this.getRefreshToken();
  }

  // 检查令牌是否过期
  isTokenExpired() {
    const token = this.getAccessToken();
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      // 添加 5 分钟的缓冲时间
      return payload.exp * 1000 < Date.now() + 5 * 60 * 1000;
    } catch (error) {
      return true;
    }
  }

  // 获取认证头
  getAuthHeader() {
    const token = this.getAccessToken();
    return token ? `Bearer ${token}` : '';
  }

  // 修改密码
  async changePassword(oldPassword, newPassword) {
    try {
      // 调用云函数修改密码
      const result = await callFunction('changePassword', {
        oldPassword,
        newPassword
      });
      if (result.code === 0) {
        // 密码修改成功后，直接清除登录状态
        this.logout();
        return {
          success: true,
          message: '密码修改成功，请使用新密码重新登录',
          requireRelogin: true
        };
      } else {
        return { 
          success: false, 
          message: result.message 
        };
      }
    } catch (error) {
      console.error('Change password error:', error);
      return { 
        success: false, 
        message: error.message || '密码修改失败，请稍后重试' 
      };
    }
  }
}

export const authService = new AuthService(); 